import { formatChartParamsDate, listTimeTitleFunc } from '@op/components/ChartCommon/formatter';
import EChartCommon from '@op/components/EChartCommon';
import { chartAllColors, NO_DATE } from '@op/constants';
import useLanguage from '@op/hooks/useLanguage';
import { getChartPosition, getXAxisAxisLabel } from '@op/utils/chart';
import { DBO_EC, DBO_Y } from '@op/utils/format';
import { reviewsFormat } from '@op/utils/value';
import React, { useEffect, useMemo, useRef } from 'react';
import { IRealtimeMentionsChartType } from '../../type';
import { DataZoom } from '@/pages/dashboard/components/Chart/_config/plug';
import { addRadarSymbol, getAccumulatedChartTimeParams } from '../../utils';
import { getChartTimeParams } from '@op/utils/time';
import { goStatsDetailAction } from '@op/pages/stats/Detail';
import { EXTRA_FILTER_SENTIMENT } from '../../constant';

interface IRealtimeChannelLanguageChart {
  chartType: string;
  data: any;
  formatDate?: (val: string) => string;
  metrics: {
    value: IRealtimeMentionsChartType;
    name: string;
    color: string;
    format?: (value: number) => string | number;
  }[];
  filter: any;
  isPercent?: boolean;
  formatValue?: (val: number) => string | number;
  needRadar?: boolean;
  isMinutes?: boolean;
  needTotal?: boolean;
  title?: string;
  chartTitle?: string;
  showTotal?: boolean;
  height?: number;
  isAccumulated?: boolean;
  showTooltipLink?: boolean;
}
export default function Index(props: IRealtimeChannelLanguageChart) {
  const {
    chartType,
    data,
    metrics,
    filter,
    formatValue = reviewsFormat,
    isPercent = false,
    needRadar = false,
    isMinutes = false,
    showTotal = false,
    title = 'Mentions',
    chartTitle,
    height = 366,
    isAccumulated = false,
    showTooltipLink = true
  } = props;
  const { formatMessage } = useLanguage();
  const divElementRef = useRef<HTMLDivElement>(null);
  const chartRealType = useMemo(() => {
    return chartType === 'stack_line' ? 'line' : chartType;
  }, [chartType]);


  useEffect(() => {
    function clickEventItem(event: any) {
      const { target } = event;
      const start_time = target?.getAttribute('data-StartTime');
      const end_time = target?.getAttribute('data-EndTime');
      const url = target?.getAttribute('data-url');
      if (start_time && end_time && url === 'feeds') {
        const { tab_type, many_hour_ago, need_many_hour_ago, by_type, ...restFilter } = filter;
        const extraFilter = EXTRA_FILTER_SENTIMENT[by_type || tab_type] || {};

        goStatsDetailAction({
          path: `/opinion/Feeds/Feeds?tab=socialMedia`,
          params: { ...restFilter, start_time, end_time, ...extraFilter },
          history,
        });
      }
    }

    if (divElementRef.current) {
      divElementRef.current.addEventListener('click', clickEventItem);
    }

    return () => {
      if (divElementRef.current) {
        divElementRef.current.removeEventListener('click', clickEventItem);
      }
    };
  }, [filter]);


  const options = useMemo(() => {
    const { series: seriesData, legend: legendData, xAxis: xAxisData } = data || {};
    const legend = legendData?.map((val: string) => {
      const { name, color } = metrics.find((metric) => metric.value === val) || {};
      return {
        name,
        itemStyle: { color },
      };
    }) || [];
    const series = seriesData?.map((val: { name: string; data: number[] }, index: number) => {
      const { name: seriesKey, data } = val;
      const { color = chartAllColors[index], name } = metrics.find((metric) => metric.value === seriesKey) || {};
      const item = {};
      if (['bar', 'stack_line'].includes(chartType)) {
        if (showTotal) {
          item.stack = 'Total';
        }
        if (chartType === 'stack_line') {
          item.areaStyle = DBO_EC.gradientColor(color, 0, 0.4);
        }
      }
      return {
        name,
        itemStyle: { color },
        data,
        type: chartRealType,
        ...item,
      };
    }) || [];

    let options: any = {
      series,
      legend: {
        data: legend,
        formatter: (params: string) => formatMessage({ id: params })
      },
      tooltip: {
        className: 'dbo-mentions-sentiment-box-tooltip-wrapper',
        formatter: (params: any) => {
          const time = params[0]?.axisValue;
          const leftTitle = listTimeTitleFunc(time, filter?.date_type || 'hourly');
          const { start_time, end_time } = getAccumulatedChartTimeParams(time, isAccumulated);
          // const rightTitle = chartLeftTitle;
          return formatChartParamsDate({
            title: leftTitle,
            // rightTitle,
            params,
            isSort: true,
            listTitleFunc: (item: any) => formatMessage({ id: item.seriesName }),
            valueFormat(value) {
              return formatValue(value);
            },
            dealwithRecords: (records, total) => {
              const list = records.map((item) => {
                if (item.originValue === 0) return item;
                // const percent = needPercent ? `(${getPercentageInfo(
                //   item.originValue,
                //   total,
                //   2,
                // )})` : '';
                return {
                  ...item,
                  value: `${item.value}`,
                };
              });

              if (showTotal) {
                list.unshift({
                  text: formatMessage({ id: title || NO_DATE.EMPTY }),
                  value: formatValue(total) as any,
                  originValue: total,
                  originText: title as any,
                });
              }
              return list;
            },
            footerText: !showTooltipLink ? undefined : `<div class="chart-common-formatter-links" data-url="feeds" data-StartTime="${start_time}" data-EndTime="${end_time}"> ${formatMessage({ id: 'Click to go to [Feeds]' })}</div>`
          });
        },
        enterable: true,
        confine: false,
        position: (
          pos: [any, any],
          params: any,
          dom: any,
          rect: any,
          size: { viewSize: [any, any] },
        ) => {
          const value = getChartPosition(
            pos,
            params,
            dom,
            rect,
            size,
            100,
            0,
            'bottom',
            3,
          );
          return value;
        },
      },
      xAxis: {
        axisLabel: getXAxisAxisLabel(filter),
        data: xAxisData || [],
      },
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => {
              return DBO_Y.reviewsPercent(value, isPercent);
            },
            color: '#838697',
          },
          nameTextStyle: {
            color: '#444444',
            fontWeight: 400,
            fontSize: '12px',
          },
          splitNumber: 5,
        },
      ],
      grid: {
        top: 20,
        left: 20,
        right: 20,
      }
    };

    if (needRadar && chartRealType === 'line') {
      options = addRadarSymbol(options, formatValue);
    }

    if (isMinutes) {
      const { legend } = options;
      options = DataZoom(options);
      options = {
        ...options,
        legend: {
          ...legend,
          bottom: 30
        },
        grid: {
          top: 20,
          bottom: 50,
          left: 10,
          right: 10,
        }
      };
    }

    return options;
  }, [chartType, data, formatMessage]);


  return (
    <div ref={divElementRef}>
      <EChartCommon
        optionsProps={{ type: chartRealType }}
        height={height}
        leftTitle={formatMessage({ id: chartTitle || title || NO_DATE.EMPTY })}
        options={options}
      ></EChartCommon>
    </div>
  );
}